import MultiReading, { MultiReadingItem } from "@/components/multi-reading"
import SingleReading from "@/components/single-reading"
import { findGradeRangeLabel } from "@/lib/form/hooks"
import {
  FormParamStatus,
  ResampledData,
  SingleValueParamData,
  WidgetBaseProps,
  WindowData,
} from "@/lib/form/types"
import { ParamSchema, ParamSchemaType, ParamWindow } from "@/lib/form/schema"
import { format } from "date-fns"
import { useEffect, useId, useMemo, useState } from "react"
import { useFormContext } from "@/lib/form/form-context"
import { useFieldError, useFormEmit, useWatch } from "@/lib/form/form"
import { ErrorFlagState } from "@/components/ui/input"
import { TZDate } from "@date-fns/tz"
import { LimitValidationError } from "@/lib/validators/limit"
import { isNil } from "lodash"
import { isFilled } from "@/ipqc/lib/utils"

export interface LimitWidgetProps extends WidgetBaseProps<ParamSchema> {
  className?: string
}

export default function LimitWidget({
  className,
  schema,
  path,
}: LimitWidgetProps) {
  const id = useId()
  const paramPath = path
  const {
    form,
    getFieldPath,
    readOnly: ctxReadOnly,
    sheetHasActiveBreakdown,
  } = useFormContext()

  const singleParamError = useFieldError({
    names: [paramPath],
    form: form,
  })[0]

  const singleParam = useWatch({
    form: form,
    name: paramPath,
    defaultValue: form.getValues(paramPath) || {},
    deep: 1,
  })
  const windowValues = useWatch({
    form,
    name: `${paramPath}.window`,
    deep: 2,
    defaultValue: [],
  })
  const singleParamRemark = useWatch({
    form,
    name: `${paramPath}.remark`,
    defaultValue: "",
  })
  const windowErrors = useFieldError({
    form,
    names: schema.window
      ? schema.window?.map((__, index) => `${paramPath}.window[${index}]`)
      : [],
  })

  const inputs = useInputs(schema)
  const emit = useFormEmit(form)

  const multiValues: string[] = useMemo(() => {
    if (!windowValues?.length) {
      return []
    }

    return windowValues.map((window: WindowData) =>
      isNil(window?.value) ? "" : window.value
    )
  }, [windowValues])

  const multiResampledValues = useMemo(() => {
    if (!windowValues?.length) {
      return []
    }

    return windowValues.map((window: WindowData) =>
      isFilled(window?.resample?.value) ? window?.resample?.value : ""
    )
  }, [windowValues])

  function getFlagState(
    data: {
      remark?: string
      resample?: ResampledData<WindowData | SingleValueParamData>
    },
    err = singleParamError
  ) {

    if (data?.resample) {
      return ErrorFlagState.Resample
    } else if (data?.remark) {
      return ErrorFlagState.Filled
    } else if (err && err.includes(LimitValidationError.OutOfRange)) {
      return ErrorFlagState.Unfilled
    }

    return ErrorFlagState.None
  }

  function inputId(schemaId: string) {
    return `${id}.${schemaId}`
  }

  function formatLabel() {
    if (!schema.unit) {
      return schema.name
    }

    return `${schema.name} (${schema.unit})`
  }

  function formatTime(str: string | undefined) {
    if (!str) {
      return ""
    }

    return format(new TZDate(str, "UTC"), "HH:mm")
  }

  function formatInpuHelperText(window?: ParamWindow): string {
    if (!window) {
      return ""
    }

    return `${formatTime(window?.startTime)} - ${formatTime(window?.endTime)}`
  }

  function useInputs(schema?: ParamSchema) {
    const [inputs, setInputs] = useState<MultiReadingItem[]>([])

    useEffect(() => {
      if (!schema?.window?.length || !windowValues?.length) {
        setInputs([])

        return
      }

      setInputs(
        schema.window.map((window, index) => {
          const currValue = windowValues.at(index)
          const error = windowErrors.at(index)
          const currError = error || ""

          return {
            id: `${index}`,
            helpText: formatInpuHelperText(window),
            error: currError,
            indicator: findGradeRangeLabel(
              currValue?.value,
              schema.limit?.gradeRanges || []
            ),
            status: currValue?.status,
            flagState: getFlagState(currValue, error),
            overtime: overtimeText(currValue, window),
          }
        })
      )
    }, [schema, windowValues, windowErrors])

    return inputs
  }

  function overtimeText(value: WindowData, window: ParamWindow): string {
    try {
      if (!value.recordedAt || !isFilled(value.value)) {
        return ""
      }

      const start = new TZDate(window.startTime, "UTC")
      const end = new TZDate(window.endTime, "UTC")
      const recordedAt = new Date(value.recordedAt)

      const startTime = start.getHours() * 60 + start.getMinutes()
      const endTime = end.getHours() * 60 + end.getMinutes()
      const recordedAtTime =
        recordedAt.getHours() * 60 + recordedAt.getMinutes()

      if (endTime > startTime) {
        if (recordedAtTime < startTime) {
          return "Early"
        }

        if (!(recordedAtTime >= startTime && recordedAtTime <= endTime)) {
          return "Overtime"
        }

        return ""
      }

      if (recordedAtTime < startTime && recordedAtTime > endTime) {
        return "Early"
      }

      if (!(recordedAtTime > startTime || recordedAtTime < endTime)) {
        return "Overtime"
      }

      return ""
    } catch {
      return ""
    }
  }

  function handleSingleChange(value: string) {
    const existing = (singleParam || {}) as SingleValueParamData

    form.setValue(
      paramPath,
      {
        ...existing,
        id: existing.id || schema.id,
        value,
        status: FormParamStatus.DRAFT,
        recordedAt: new Date().toISOString(),
      },
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }
    )
  }

  function handleMultiChange(value: string[], changedIndex: number) {
    inputs.forEach((__input, i) => {
      if (i !== changedIndex) {
        return
      }

      const gradeLabel = findGradeRangeLabel(
        value[i],
        schema.limit?.gradeRanges || []
      )
      const existingWindow = (windowValues?.at(i) || {}) as WindowData

      const newValue = {
        ...existingWindow,
        value: value[i],
        status: FormParamStatus.DRAFT,
        recordedAt: new Date().toISOString(),
        startTime: existingWindow.startTime || schema.window?.at(i)?.startTime,
        endTime: existingWindow.endTime || schema.window?.at(i)?.endTime,
        gradeLabel: gradeLabel,
        id: existingWindow.id || schema.window?.at(i)?.id,
      }
      form.setValue(getFieldPath(paramPath, `window[${i}]`), newValue, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      })
    })
  }

  function handleClickErrorFlagOnMulti(__value: string[], index?: number) {
    emit("clickParamRemarks", {
      path: `${path}.window[${index}]`,
      schema,
    })
  }

  function handleClickErrorFlagOnSingle() {
    emit("clickParamRemarks", {
      path,
      schema,
    })
  }

  function getInputTypeProps() {
    if (schema.type === ParamSchemaType.VALUE_NUMBER) {
      return {
        type: "text",
        inputMode: "numeric",
      } as const
    }

    return {}
  }

  function handleFocusSingleRemark() {
    if (schema.formula && !sheetHasActiveBreakdown) {
      emit("showFormulaInput", {
        path,
        schema,
      })
    }
  }

  function handleFocusMultReading(__: string[], index: number) {
    if (schema.formula && !sheetHasActiveBreakdown) {
      emit("showFormulaInput", {
        path: `${path}.window[${index}]`,
        schema,
      })
    }
  }

  function handleClickSingleSubmittedFlag() {
    emit("clickShowRemarkDetails", {
      path: path,
      schema: schema,
      payload: singleParam,
    })
  }

  function handleClickWindowSubmittedFlag(index: number) {
    emit("clickShowRemarkDetails", {
      path: `${path}.window[${index}]`,
      schema: schema,
      payload: windowValues?.at(index),
    })
  }

  function handleClickSingleResampleFlag() {
    emit("clickShowResampleDetails", {
      path: path,
      schema: schema,
      payload: singleParam,
    })
  }

  function handleClickWindowResampleFlag(index: number) {
    emit("clickShowResampleDetails", {
      path: `${path}.window[${index}]`,
      schema: schema,
      payload: windowValues?.at(index),
    })
  }

  if (!schema.window || schema.window.length < 2) {
    return (
      <SingleReading
        id={inputId(schema.id ?? '')}
        className={className}
        direction={schema.mode === "TEST" ? "row" : "column"}
        label={formatLabel()}
        // labelHelpText={schema.subtext || formatLimit()}
        value={singleParam?.value}
        resampledValue={singleParam?.resample?.value}
        flagState={getFlagState({
          remark: singleParamRemark,
          resample: singleParam?.resample,
        })}
        status={singleParam?.status}
        disabled={!!sheetHasActiveBreakdown}
        showNaForUnresolved={!!sheetHasActiveBreakdown}
        readOnly={!!schema.formula || !!ctxReadOnly}
        {...getInputTypeProps()}
        onClickErrorFlag={handleClickErrorFlagOnSingle}
        onClickSubmittedErrorFlag={handleClickSingleSubmittedFlag}
        onClickResampleFlag={handleClickSingleResampleFlag}
        onChange={handleSingleChange}
        onFocus={handleFocusSingleRemark}
      />
    )
  }

  return (
    <MultiReading
      id={inputId(schema.id ?? '')}
      className={className}
      label={formatLabel()}
      // labelHelpText={schema.subtext || formatLimit()}
      inputs={inputs}
      value={multiValues}
      resampledValue={multiResampledValues}
      direction={schema.mode === "TEST" ? "row" : "column"}
      disabled={!!sheetHasActiveBreakdown}
      showNaForUnresolved={!!sheetHasActiveBreakdown}
      readOnly={!!schema.formula || !!ctxReadOnly}
      {...getInputTypeProps()}
      onClickErrorFlag={handleClickErrorFlagOnMulti}
      onClickSubmittedErrorFlag={handleClickWindowSubmittedFlag}
      onClickSubmittedResampleFlag={handleClickWindowResampleFlag}
      onChange={handleMultiChange}
      onFocus={handleFocusMultReading}
    />
  )
}
